import { Component } from '@angular/core';
import Highcharts from 'highcharts';
import WordcloudModule from 'highcharts/modules/wordcloud';
import { chartColors } from '../report-aggregation';
import { CHART_SPACING } from './chart-layout';
import { ChartBlockBase } from './chart-block-base';

// The only optional Highcharts module this library registers. Called at module load rather than in
// a component, matching how the workspace does it elsewhere (schedule.component.ts), and idempotent
// -- Highcharts skips a series type it already has, so several blocks importing this is harmless.
WordcloudModule(Highcharts);

/** Words drawn when the block does not say. The one setting that decides how long layout takes. */
const DEFAULT_MAX_WORDS = 60;

/** Respondents a word needs before it is drawn. Two, so one person's typo is never a word. */
const DEFAULT_MIN_COUNT = 2;

/** Below this a word is punctuation or an abbreviation, not a finding. */
const MIN_WORD_LENGTH = 3;

/**
 * The cloud's own box. A cloud has no rows, so it does not grow with the data the way a bar chart
 * does -- the height is a judgement about how much room the words need to spiral out into. Given
 * more space Highcharts scales the whole cloud up rather than adding white space, so a taller card
 * is a bigger cloud rather than an emptier one.
 */
const MIN_HEIGHT = 420;
const MAX_HEIGHT = 760;

/**
 * Common English words, removed always.
 *
 * Kept deliberately short: it covers the words that are noise in *every* survey, and nothing that
 * could be a finding in one. Anything study-specific belongs in the block's own `stopwords`, where
 * somebody has decided it -- a list that quietly grew to include "cost" or "service" would be
 * removing findings from decks nobody was looking at.
 */
const STOPWORDS = new Set([
  'the', 'and', 'for', 'that', 'this', 'with', 'you', 'your', 'are', 'was', 'were', 'but', 'not',
  'have', 'has', 'had', 'they', 'them', 'their', 'there', 'then', 'than', 'from', 'what', 'when',
  'where', 'which', 'who', 'whom', 'how', 'why', 'all', 'any', 'some', 'can', 'could', 'would',
  'should', 'will', 'just', 'like', 'about', 'more', 'most', 'other', 'into', 'over', 'also',
  'only', 'very', 'much', 'many', 'such', 'its', 'our', 'ours', 'his', 'her', 'hers', 'him',
  'she', 'him', 'been', 'being', 'does', 'did', 'doing', 'done', 'get', 'got', 'because', 'while',
  'these', 'those', 'here', 'out', 'off', 'own', 'too', 'him', 'one', 'two', 'now', 'way', 'say',
  'says', 'said', 'see', 'make', 'made', 'thing', 'things', 'something', 'anything', 'everything',
  'nothing', 'someone', 'anyone', 'everyone', 'really', 'still', 'even', 'ever', 'never', 'always',
  'yes', 'not', 'sure', 'know', 'think', 'want', 'need', 'use', 'used', 'using', 'lot', 'bit',
  'etc', 'per', 'via', 'upon', 'each', 'both', 'few', 'less', 'least', 'able', 'may', 'might',
  'must', 'shall', 'let', 'put', 'take', 'takes', 'taken', 'give', 'gives', 'given', 'well',
]);

/** One word, as the cloud draws it. */
interface Word {
  /** The spelling most respondents used, so a proper noun keeps its capital. */
  text: string;
  /** Respondents who used it, not times it was typed. */
  count: number;
}

/** What the count depends on, so the cache knows when it has gone stale. */
interface CountSettings {
  minCount: number;
  maxWords: number;
  stop: Set<string>;
}

/**
 * Word cloud: the words respondents used in one open-ended question, sized by how many used them.
 *
 * Where the verbatim explorer lets a reader work through the answers, this is the shape of them at
 * a glance. Nothing here is a percentage: a word's size is a respondent count, so the cloud says
 * what was talked about rather than how much of the sample agreed with anything.
 *
 * **Counting is cached, and that matters more than it looks.** `ChartBlockBase.build()` re-runs on
 * every resize past a few pixels and again when the webfont loads, so counting inside it would
 * re-read every answer several times a second while somebody dragged a window. The answers are
 * recounted only when the series or the settings actually change; a resize re-lays out the words it
 * already has.
 */
@Component({
  selector: 'app-word-cloud-block',
  standalone: false,
  template: `
    @if (chartOptions) {
      <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="chartOptions"
                        style="width: 100%; display: block;"></highcharts-chart>
    }

    <!-- A cloud is the least readable module in the deck to anyone not looking at it, so the words
         are also listed in order. Every other Highcharts block here has no text alternative; this
         one earns it. -->
    <p class="sr-only">{{ summary }}</p>

    @if (note) {
      <p class="grey4 nav-font12 mb-0">{{ note }}</p>
    }
  `,
  // Inline by default, which would make the width measured in ChartBlockBase 0.
  styles: [`
    :host { display: block; }

    /* The app-wide theme fills .highcharts-background from a CSS variable, and CSS beats the fill
       attribute Highcharts writes from its backgroundColor option -- the same trap report-block-host
       documents for .highcharts-point. A cloud is words on a page rather than a plot, so it sits
       straight on the card. Scoped here: every other chart in the deck keeps its panel. */
    :host ::ng-deep .highcharts-background { fill: transparent; }
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `],
})
export class WordCloudBlockComponent extends ChartBlockBase {
  readonly highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options | null = null;
  summary = '';
  note = '';

  /** The answers these words were counted from, and the settings they were counted under. */
  private countedFrom?: readonly string[];
  private countedUnder = '';
  private words: Word[] = [];
  /** Respondents who answered at all, for the share in the tooltip. */
  private answered = 0;

  protected build() {
    this.chartOptions = null;
    this.summary = '';
    this.note = '';
    if (this.block.reportType !== 'word-cloud') return;

    const series = this.series?.[0];
    if (!series?.known) {
      this.note = series ? `No data for ${series.name}.` : '';
      return;
    }

    const verbatims = series.verbatims ?? [];
    this.answered = verbatims.length;
    this.countWords(verbatims, series.name);

    if (!this.words.length) {
      this.note = this.answered
        ? 'No word here was used by enough people to draw.'
        : 'Nobody answered this question in the current view.';
      return;
    }

    this.summary = `${this.words.length} words from ${this.answered} answers, most used first: `
      + this.words.map(word => `${word.text} ${word.count}`).join(', ');

    const roles = chartColors(this.palette, 10);
    const answered = this.answered;

    this.chartOptions = this.deckMotion({
      chart: {
        height: Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, 420 + this.words.length * 4)),
        spacing: CHART_SPACING,
        // Not what actually clears it -- see the stylesheet below, where the app-wide theme has to
        // be overridden -- but left set so the option and the CSS cannot say different things.
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        // The real count, off `custom`. `weight` is the drawing size, not the figure -- see sized().
        pointFormat: '<b>{point.custom.count}</b> of ' + answered + ' who answered'
          + ' ({point.custom.share}%)',
      },
      series: [{
        type: 'wordcloud',
        name: 'Respondents',
        // Every word horizontal. Highcharts turns some of them on their side by default, which
        // reads as decoration and costs a client half a second per rotated word.
        rotation: { from: 0, to: 0, orientations: 1 },
        spiral: 'rectangular',
        // Highcharts scales the finished cloud to fill its box, so these set the *spread* between
        // the busiest word and the rest more than the absolute size. A narrower spread reads as a
        // more even cloud; the lever for smaller words overall is drawing more of them.
        minFontSize: this.compact ? 7 : 10,
        maxFontSize: this.compact ? 22 : 32,
        style: { fontFamily: 'inherit', fontWeight: '600' },
        data: this.words.map((word, index) => ({
          name: word.text,
          weight: sized(word.count, this.words[0].count),
          colorIndex: index % roles.length,
          color: roles[index % roles.length],
          custom: {
            count: word.count,
            share: Math.round((word.count / Math.max(1, answered)) * 100),
          },
        })),
      }],
    });
  }

  /**
   * Recount only when the answers or the settings changed.
   *
   * Keyed on the array's identity rather than its contents: `seriesForBlock` builds a new array
   * every time the filter rail moves and hands back the same one otherwise, which is exactly the
   * question being asked. The settings go in as a string because they are a handful of scalars and
   * comparing them any other way is more code than it saves.
   */
  private countWords(verbatims: readonly string[], question: string) {
    if (this.block.reportType !== 'word-cloud') return;
    const extra = this.block.reportExtraData;

    const maxWords = positive(extra.maxWords, DEFAULT_MAX_WORDS);
    const minCount = positive(extra.minCount, DEFAULT_MIN_COUNT);
    const stop = new Set(STOPWORDS);
    for (const word of extra.stopwords ?? []) {
      const key = word.trim().toLowerCase();
      if (key) stop.add(key);
    }
    // Respondents echo the question back, so its own words are the largest in the cloud and say
    // nothing. Removed unless the block asks for them -- a question about a brand is the case where
    // the brand name really is the finding.
    if (!extra.useQuestionWords) {
      for (const word of tokens(question)) stop.add(word);
    }

    const signature = `${maxWords}|${minCount}|${extra.useQuestionWords ? 1 : 0}|`
      + `${[...stop].join(' ')}`;
    if (verbatims === this.countedFrom && signature === this.countedUnder) return;

    this.words = count(verbatims, { maxWords, minCount, stop });
    this.countedFrom = verbatims;
    this.countedUnder = signature;
  }
}

/**
 * A respondent count as a drawing weight.
 *
 * Highcharts sizes a word as `weight / largest weight × maxFontSize`, floored at `minFontSize` --
 * **strictly proportional to the weight**, not interpolated between the two sizes. Handing it raw
 * counts therefore hands it the raw spread, and open-ended answers are Zipf-shaped: on the Q24
 * fixture the top word is used by 255 of 460 respondents and the sixtieth by 17. Proportionally
 * that is 32px against 2px, so one word came out enormous and half the cloud sat squashed on the
 * floor, indistinguishable from each other.
 *
 * The square root is the usual answer, and it is the honest one here: what a reader compares
 * between two words is the *area* they take up, which goes as the square of the font size, so
 * area stays proportional to the count while the heights come closer together. The same fixture
 * becomes 32px against 10px, spread smoothly rather than as a cliff.
 *
 * It does mean `weight` is no longer the figure. The count travels on `point.custom.count`, which
 * is what the tooltip reads.
 */
function sized(count: number, largest: number): number {
  return Math.sqrt(count / Math.max(1, largest));
}

function positive(value: number | undefined, fallback: number): number {
  const whole = Math.trunc(value ?? fallback);
  return Number.isFinite(whole) && whole > 0 ? whole : fallback;
}

/** Lowercased words of at least the minimum length. Apostrophes kept: "don't" is one word. */
function tokens(text: string): string[] {
  return text.toLowerCase()
    .split(/[^a-z0-9']+/)
    .filter(word => word.length >= MIN_WORD_LENGTH);
}

/**
 * The words, most-used first.
 *
 * A word counts **once per respondent** however many times they typed it, so one long answer
 * repeating itself cannot make a word the biggest thing on the page. The spelling kept is the one
 * most respondents used, so "America" is not printed "america" -- the same courtesy the verbatim
 * explorer pays by showing an answer as typed.
 */
function count(verbatims: readonly string[], settings: CountSettings): Word[] {
  const counts = new Map<string, number>();
  const spellings = new Map<string, Map<string, number>>();

  for (const answer of verbatims) {
    const seen = new Set<string>();
    for (const word of tokens(answer)) {
      if (settings.stop.has(word) || seen.has(word)) continue;
      seen.add(word);
      counts.set(word, (counts.get(word) ?? 0) + 1);
    }

    // Surface forms are tallied separately: the same respondent writing "Energy" and "energy" is
    // one respondent for the count, but both spellings are evidence of which to print.
    for (const raw of answer.split(/[^A-Za-z0-9']+/)) {
      const key = raw.toLowerCase();
      if (key.length < MIN_WORD_LENGTH || !seen.has(key)) continue;
      const forms = spellings.get(key) ?? new Map<string, number>();
      forms.set(raw, (forms.get(raw) ?? 0) + 1);
      spellings.set(key, forms);
    }
  }

  return [...counts.entries()]
    .filter(([, used]) => used >= settings.minCount)
    // Most used first, and alphabetical where two are level, so the cloud is the same twice running.
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, settings.maxWords)
    .map(([key, used]) => ({ text: commonest(spellings.get(key), key), count: used }));
}

/** The spelling most respondents used, or the lowercased word when nothing was recorded. */
function commonest(forms: Map<string, number> | undefined, fallback: string): string {
  if (!forms?.size) return fallback;
  let best = fallback;
  let bestCount = -1;
  for (const [form, used] of forms) {
    if (used > bestCount) {
      best = form;
      bestCount = used;
    }
  }
  return best;
}
